import { ClientTheme, OrderListPageEnum } from "@/interfaces/enums";
import rfStore from "./rfStore";
import { isLowIosV15 } from "./util";

class ClientDeepLinks {
	private readonly scheme = process.env.NEXT_PUBLIC_DEEP_LINK_SCHEME;

	loginPage(inviteCode: string) {
		return this.scheme + "://client/deeplink?target=login" + getSourceId() + "&inviteCode=" + inviteCode;
	}

	// 跳转到首页
	home() {
		return this.scheme + "://client/deeplink?target=home" + getSourceId();
	}

	// 资产TAb
	assetPage(extra = "") {
		return this.scheme + "://client/deeplink?target=asset" + getSourceId() + extra;
	}

	// 存入资金TAb
	depositPage() {
		return this.scheme + "://client/deeplink?target=deposit" + getSourceId();
	}

	// 电汇
	wireTransfer(currency: string, price: number) {
		return `${this.scheme}://client/deeplink?target=wireTransfer${getSourceId()}&currency=${currency}&amount=${price}`;
	}

	// 客服
	customer() {
		return `${this.scheme}://client/deeplink?target=customerservice${getSourceId()}`;
	}

	// 选择价格
	depositAmount(currency?: string, amount?: number) {
		return `${this.scheme}://client/deeplink?target=deposit_amount&currency=${currency}&amount=${amount}${getSourceId()}`;
	}

	// 期权tab
	optiontab() {
		return `${this.scheme}://client/deeplink?target=option${getSourceId()}`;
	}

	// 存钱罐
	moneyBox() {
		return `${this.scheme}://client/deeplink?target=moneyBox${getSourceId()}`;
	}

	/**
	 * 抄底宝精选 tag && 抄底宝详情 && 抄底宝购买页 symbol
	 * 0是公司抄底宝 1是指数抄底宝
	 */
	betterBuyOmnibus(tab?: string, symbol?: string, extra = "") {
		let schemaUrl = `${this.scheme}://client/deeplink?target=option_betterBuy${getSourceId()}`;
		if (tab) {
			schemaUrl += `&tab=${tab}`;
		}
		if (symbol) {
			schemaUrl += `&symbol=${symbol}`;
		}
		if (extra) {
			schemaUrl += extra;
		}
		return schemaUrl;
	}
	// 抄底宝详情
	betterBuyDetail(symbol: string, market: string) {
		return `${this.scheme}://client/deeplink?target=stockOption_betterBuy&symbol=${symbol}&market=${market}${getSourceId()}`;
	}

	// 股票详情
	stockDetail(market: string, symbol: string, extra = "") {
		return `${this.scheme}://client/deeplink?target=stockDetail&market=${market}&symbol=${symbol}${extra}${getSourceId()}`;
	}

	// 期权详情
	optionDetail(symbol: string, extra = "") {
		return `${this.scheme}://client/deeplink?target=option_detail&symbol=${symbol}${extra}${getSourceId()}`;
	}

	// 个人主页
	userHonePage(userId: string, extra = "") {
		return `${this.scheme}://client/deeplink?target=userHomePage&userId=${userId}&momentId=0${extra}${getSourceId()}`;
	}

	// 股单详情
	watchListDetail(watchListId: string, extra = "") {
		return `${this.scheme}://client/deeplink?target=watchListDetail&id=${watchListId}${extra}${getSourceId()}`;
	}

	// 入金问卷前一页
	depositQuestionsAnswer() {
		return `${this.scheme}://client/deeplink?target=depositQuestionsAnswer${getSourceId()}`;
	}

	// 订单详情
	orderDetail(orderId: string) {
		return `${this.scheme}://client/deeplink?target=orderDetail&orderId=${orderId}${getSourceId()}`;
	}
	// 订单列表
	orderList(type: OrderListPageEnum) {
		return `${this.scheme}://client/deeplink?target=orderList&type=${type}${getSourceId()}`;
	}
	// 人脸识别
	aliIdVerify() {
		return `${this.scheme}://client/deeplink?target=aliIdVerify${getSourceId()}`;
	}

	// 社群
	community() {
		return `${this.scheme}://client/deeplink?target=community${getSourceId()}`;
	}

	// 理财产品订单详情
	financialOrderDetail(orderId: string) {
		return `${this.scheme}://client/deeplink?target=financialOrderDetail&orderId=${orderId}${getSourceId()}`;
	}

	// 牛人榜单
	rockStarRankList(tab: number | string, extra = "") {
		return `${this.scheme}://client/deeplink?target=rockStarRankList&tab=${tab}${extra}${getSourceId()}`;
	}

	// 股票抄底宝
	stockButterBuy(symbol: string, market: string, extra = "") {
		return `${this.scheme}://client/deeplink?target=stockOption_betterBuy&symbol=${symbol}&market=${market}${extra}${getSourceId()}`;
	}

	// 小牛｜小熊列表页  call 1 小牛 0 小熊
	babyBullAndBear(symbol: string, market: string, call: "1" | "0") {
		return `${this.scheme}://client/deeplink?target=option_filter&symbol=${symbol}&market=${market}&call=${call}${getSourceId()}&condition=`;
	}

	// gpt list
	tradeGPTList(extra = "") {
		return `${this.scheme}://client/deeplink?target=tradeGpt${extra}${getSourceId()}`;
	}
	tradeGPTDetail(feedId: string, extra = "") {
		return `${this.scheme}://client/deeplink?target=gptDetail&feedId=${feedId}${extra}${getSourceId()}`;
	}

	// 小熊保险
	positionSmartGuard(symbol: string, market: string) {
		return `${this.scheme}://client/deeplink?target=positionSmartGuard&symbol=${symbol}&market=${market}${getSourceId()}`;
	}

	// webview
	/**
	 * rightBtnType: 显示客服
	 * @param {Boolean | String} useDocTitle
	 * 是否使用document title 或者自定义内容
	 * 作为 navigation title
	 */
	webview(url: string, rightBtnType?: ClientHeadBtnTypes, useDocTitle?: boolean | string) {
		let encodeRightBtnParams = "";
		if (rightBtnType === ClientHeadBtnTypes.customerservice) {
			encodeRightBtnParams = encodeURIComponent(getClientCustomerBtnParams(this.customer()));
		}

		// 不传参数client 读document title
		let titleQuery = useDocTitle ? "" : `&title= `;
		if (typeof useDocTitle === "string") {
			titleQuery = `&title=${useDocTitle}`;
		}

		// rightBar query
		let rightBarQuery = "&rightBtn="; // FIXME: 客户端链接链接判断 否则不走webview 逻辑
		if (encodeRightBtnParams) {
			rightBarQuery = `&rightBtn=${encodeRightBtnParams}`;
		}

		const isLowIos = isLowIosV15();
		if (isLowIos) {
			rightBarQuery = "&rightBtn=";
		}

		return `${
			this.scheme
		}://client/deeplink?target=webview${getSourceId()}&forceLogin=1${titleQuery}&url=${encodeURIComponent(
			url,
		)}${rightBarQuery}`;
	}

	// FIXME: 打开无头的网页 无法实现
	webviewOutApp(url: string) {
		return `${
			this.scheme
		}://client/deeplink?target=webview&forceLogin=1${getSourceId()}&title= &url=${encodeURIComponent(url)}`;
	}

	// 打开三方支付页面
	webviewThirdParty(url: string) {
		return `${this.scheme}://client/deeplink?target=webview&url=${encodeURIComponent(url)}`;
	}

	// 卡片验证
	cardValidate(cardId: string) {
		return `${this.scheme}://client/deeplink?target=checkoutCardVerfy&cardId=${cardId}${getSourceId()}`;
	}

	custom(linkQuery: string) {
		return `${this.scheme}://client/deeplink${linkQuery}`;
	}

	// GPT Tab
	gptTab(templateId?: string) {
		let url = `${this.scheme}://client/deeplink?target=tradeGpt${getSourceId()}`;
		if (templateId) {
			url += `&templateId=${templateId}`;
		}
		return url;
	}

	// 客服群
	joinGroup() {
		return `${this.scheme}://client/deeplink?target=joinGroup${getSourceId()}`;
	}

	// 股单聚合
	watchListGroup(tagId: string, isEtf: string) {
		return `${this.scheme}://client/deeplink?target=watchListGroup&tagId=${tagId}&isEtf=${isEtf}`;
	}

	// rockBot记录
	rockBot() {
		return `${this.scheme}://client/deeplink?target=rockbot`;
	}

	// 入金记录
	depositRecord() {
		return `${this.scheme}://client/deeplink?target=depositList`;
	}

	// android 用浏览器打开
	openBrowser(url: string) {
		return `${this.scheme}://client/deeplink?target=browser&url=${encodeURIComponent(url)}`;
	}

	couponDetail(couponId: number) {
		return `${this.scheme}://client/deeplink?target=commissionCouponsDetail&id=${couponId}${getSourceId()}`;
	}
}

// 获取客户按钮参数
function getClientCustomerBtnParams(target: string) {
	const customerIcons = {
		dark: "https://web-cdn.rockflow.tech/icon/icon_customer_service_white.png",
		light: "https://web-cdn.rockflow.tech/icon/icon_customer_service.png",
	};

	const rfState = rfStore.getRFState();
	const imgIcon = rfState?.nightModel === ClientTheme.dark ? customerIcons.dark : customerIcons.light;

	return JSON.stringify({
		text: "",
		image: imgIcon,
		target: target,
		action: "jump",
	});
}

export enum ClientHeadBtnTypes {
	customerservice = "customerservice",
}

function getSourceId() {
	const rfState = rfStore.getRFState();
	if (!rfState?.model) return "&source=h5";
	return "&source=h5_in_app";
}

const clientDeepLinks = new ClientDeepLinks();
export default clientDeepLinks;
